import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Formik, Form } from 'formik'
import Loading from 'custom/assets/loaders/Loading'
import { useTranslation } from 'react-i18next'
import {
  LOAN_KEYS,
  VALIDATION_MESSAGES,
  SIDENAV_KEYS,
  PAYDAY_LOANS_KEYS,
} from '../../../utils/translation-constants'
import LoanService from '../../../services/loan.service'
import { setAccounts } from '../../../actions'
import { ApplyLoanSchema } from '../../../schemas/loan'
import InputError from '../../../components/InputError/InputError'
import NeoButton from '../../../components/NeoButton/NeoButton'
import NeoInput from '../../../components/NeoInput/NeoInput'
import NeoSuccess from '../../../components/FormComponents/NeoSuccess'
import { useHistory } from 'react-router-dom'
import NumberFormat from 'react-number-format'
import LayoutWithOutAccountCard from '../../../layout/Layout/layout-without-card'

import TermsAndConditions from '../../../components/TermsAndConditions/TermsAndCondition'
import config from 'custom/config/environment'

import validationConfig from 'custom/config/validation.config.json'
const loanConfig = validationConfig['loan']

export default function ApplyLoan() {
  const { t } = useTranslation()
  const [stage, setStage] = useState(1)
  const [requestValues, setRequestValues] = useState(null)
  const [message, setMessage] = useState('Message Placeholder')
  const [formValues, setFormValues] = useState({
    loanPurpose: '',
    sourceOfIncome: '',
    loanCurrency: '',
    incomeRange: '',
    loanAmount: '',
    tenureMonthlyOrYearly: '',
    tenureValue: '',
  })
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const fetchAccounts = useCallback(async () => {
    const x = await LoanService.getLoanAccounts()
    dispatch(setAccounts(x.accountSummaryList))
  }, [dispatch])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  useEffect(() => {
    fetchValues()
  }, [])

  const fetchValues = useCallback(async () => {
    try {
      const response = await LoanService.getLoanRequestValues()
      setRequestValues(response)
    } catch (err) {}
  }, [])

  const verifyRequestLoan = useCallback(async (data, { setSubmitting }) => {
    if (data.loanAmount === '0.00' || data.loanAmount === '.00') {
      // setMessage({ code: 'Internal', message: 'Amount should be greater than 0 ' })
      // showModal()
      return
    }
    setSubmitting(true)
    setFormValues(data)
    setSubmitting(false)
    setStage(2)
  }, [])

  const requestLoanSubmit = useCallback(async () => {
    try {
      setLoading(true)
      const response = await LoanService.requestLoan(formValues)
      if (response.status === 200) {
        setLoading(false)
        setMessage(response.data.message)
        setStage(3)
      }
    } catch (err) {
      setLoading(false)
    }
  }, [formValues])

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirm()
      case 3:
        return <NeoSuccess message={message} linkPath='/loans' />
      default:
        return renderLoading()
    }
  }

  function renderLoading() {
    return (
      <div className='d-flex flex-column text-center justify-content-center align-items-center'>
        <div className='my-4'>
          <Loading height='50px' width='50px' />
        </div>
      </div>
    )
  }

  function renderForm() {
    if (requestValues !== null) {
      return (
        <div>
          <div className='heading-style'>{t(LOAN_KEYS.SELECT_ACCOUNT_KEYS.APPLY_LOAN_TITLE)}</div>
          <Formik
            initialValues={formValues}
            validationSchema={ApplyLoanSchema}
            onSubmit={verifyRequestLoan}>
            {({ isSubmitting, values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                <div className='d-flex row'>
                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='1'
                      showAsterisk={loanConfig['isLoanAmountRequired']}
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_AMOUNT)}
                      mode='amount'
                      onChange={(values) => {
                        setFieldValue('loanAmount', values.value)
                      }}
                      name='amount'
                    />
                    <InputError text={errors.loanAmount} touched={touched.loanAmount} />
                    <InputError
                      text={t(VALIDATION_MESSAGES.LOAN_AMOUNT_INVALID)}
                      touched={values.loanAmount === '0.00' || values.loanAmount === '.00'}
                    />
                  </div>

                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='2'
                      showAsterisk={loanConfig['isLoanPurposeRequired']}
                      mode='select'
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.PURPOSE)}
                      name='loanPurpose'
                      onChange={handleChange}
                      keyValue={null}
                      list={requestValues.values['Loan Purpose']}
                    />
                    <InputError text={errors.loanPurpose} touched={touched.loanPurpose} />
                  </div>

                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='3'
                      showAsterisk={loanConfig['isTenureMonthlyOrYearlyRequired']}
                      mode='select'
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.TENURE_MONTHLY_OR_YEARLY)}
                      name='tenureMonthlyOrYearly'
                      type='number'
                      onChange={handleChange}
                      list={[t(LOAN_KEYS.NEW_LOAN_KEYS.YEAR), t(LOAN_KEYS.NEW_LOAN_KEYS.MONTH)]}
                    />
                    <InputError
                      text={errors.tenureMonthlyOrYearly}
                      touched={touched.tenureMonthlyOrYearly}
                    />
                  </div>

                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='4'
                      showAsterisk={loanConfig['isTenureValueRequired']}
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.TENURE)}
                      mode='onlyNumber'
                      onChange={(values) => {
                        setFieldValue('tenureValue', values.floatValue)
                      }}
                      name='tenure'
                    />

                    {/* <NeoInput
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.TENURE)}
                      type='number'
                      onChange={handleChange}
                      name='tenureValue'
                    /> */}
                    <InputError text={errors.tenureValue} touched={touched.tenureValue} />
                  </div>
                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='5'
                      showAsterisk={loanConfig['isSourceOfIncomeRequired']}
                      mode='select'
                      onChange={handleChange}
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.SOURCE)}
                      name='sourceOfIncome'
                      list={requestValues.values['Source of Income']}
                    />
                    <InputError text={errors.sourceOfIncome} touched={touched.sourceOfIncome} />
                  </div>
                </div>
                <div className='d-flex row'>
                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='6'
                      showAsterisk={loanConfig['isLoanCurrencyRequired']}
                      mode='select'
                      onChange={handleChange}
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.currency)}
                      name='loanCurrency'
                      list={[config.LOCAL_CURRENCY_CODE]}
                    />
                    <InputError text={errors.loanCurrency} touched={touched.loanCurrency} />
                  </div>
                  <div className='form-group col-12'>
                    <NeoInput
                      tabIndex='7'
                      showAsterisk={loanConfig['isIncomeRangeRequired']}
                      mode='select'
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.INCOME_RANGE)}
                      name='incomeRange'
                      onChange={handleChange}
                      list={requestValues.currAndRange[values.loanCurrency]}
                      waitCondition={values.loanCurrency !== ''}
                    />
                    <InputError text={errors.incomeRange} touched={touched.incomeRange} />
                  </div>
                </div>
                <div className='mt-5'>
                  {isSubmitting ? (
                    renderLoading()
                  ) : (
                    <NeoButton
                      tabIndex='8'
                      type='submit'
                      mode={'newDesign'}
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.SUBMIT)}
                      disabled={isSubmitting}
                    />
                  )}
                </div>
                <div className='back-cancel-btn-container'>
                <button className='back-cancel-btn btn btn-sm new-design-warning-text-color' tabIndex={'9'} onClick={() => history.goBack()}>
                  {' '}
                  {t(SIDENAV_KEYS.TOUCH_ID_KEYS.CANCEL)}
                </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )
    }
    return renderLoading()
  }

  function renderConfirm() {
    return (
      <div className='d-flex flex-column confirmation'>
        <h3>{t(LOAN_KEYS.SELECT_ACCOUNT_KEYS.APPLY_LOAN_CONFIRM_TITLE)}</h3>

        <div>
          <h6 className='label'>{t(PAYDAY_LOANS_KEYS.LOAN_AMOUNT)}</h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues.loanAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
            />
          </h6>
        </div>

        <div>
          <h6 className='label'>{t(PAYDAY_LOANS_KEYS.PURPOSE_OF_LOAN_DETAILS)}</h6>
          <h6 className='value'>{formValues.loanPurpose}</h6>
        </div>

        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.TENURE_MONTHLY)}</h6>
          <h6 className='value'>{formValues.tenureMonthlyOrYearly}</h6>
        </div>

        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.TENURE_DETAILS)}</h6>
          <h6 className='value'>{formValues.tenureValue}</h6>
        </div>

        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.SOURCE_INCOME)}</h6>
          <h6 className='value'>{formValues.sourceOfIncome}</h6>
        </div>
        <div>
          <h6 className='label'>{t(LOAN_KEYS.DETAILS_KEYS.currency)}</h6>
          <h6 className='value'>{formValues.loanCurrency}</h6>
        </div>
        <div>
          <h6 className='label'>{t(LOAN_KEYS.DETAILS_KEYS.INCOME_RANGE_DETAILS)}</h6>
          <h6 className='value'>{formValues.incomeRange}</h6>
        </div>
        <div>
          <TermsAndConditions module='LOANREPAY' />
        </div>
        <div className='mt-5'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode='newDesign'
              text={t(LOAN_KEYS.NEW_LOAN_KEYS.CONFIRM)}
              onClick={requestLoanSubmit}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {' '}
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
        </div>
      </div>
    )
  }

  return (
    <LayoutWithOutAccountCard breadCumKey={'APPLY_LOAN'} showEmptySpace={true} keyPath={'LOAN'}>
      <div className='new-design-form-style'>{renderSwitch()}</div>
    </LayoutWithOutAccountCard>
  )
}
